import * as amplitude from "@amplitude/analytics-node";
import {
  McpServer,
  ReadResourceTemplateCallback,
  ResourceMetadata,
  ResourceTemplate,
  ToolCallback,
} from "@modelcontextprotocol/sdk/server/mcp.js";
import { RequestHandlerExtra } from "@modelcontextprotocol/sdk/shared/protocol.js";
import {
  CallToolResult,
  ReadResourceResult,
  ServerNotification,
  ServerRequest,
  ToolAnnotations,
} from "@modelcontextprotocol/sdk/types.js";
import { ZodRawShape, ZodType, output } from "zod";
import * as Sentry from "@sentry/node";
import { ApiType, internalRequest, request } from "./api/api.js";
import { captureMessage } from "./common/errors.js";
import { log } from "./common/logger.js";
import { setActiveTool, setRequestUser } from "./common/requestContext.js";
import {
  Credentials,
  getCredentials,
  isLocalMCP,
} from "./common/credentials.js";
import { accessRestrictedMessage } from "./common/accessRestriction.js";
import packageJson from "../package.json" with { type: "json" };

let enabled = false;
export function initAmplitude() {
  const key = process.env["AMPLITUDE_API_KEY"];
  if (!key) return;

  if (process.env["NODE_ENV"] !== "dev") {
    amplitude.init(key, { minIdLength: 1 });
  }
  enabled = true;
}

const track = (...args: Parameters<typeof amplitude.track>) => {
  if (!enabled) return;

  if (process.env["NODE_ENV"] === "dev") {
    log.info("Amplitude track:", ...args);
  } else {
    amplitude.track(...args);
  }
};

interface EventProperties {
  tool_name: string;
  tool_parameters: Record<string, unknown>;
  success: boolean;
  error_message?: string;
  progress_token?: string | number | undefined;
  mcp_client_user_agent?: string;
  duration_ms?: number;
}

interface UserProperties {
  team_id?: number;
  viewer_id?: string;
  styleguide_id?: number;
  token?: string;
}

interface McpLogPayload {
  tool_name: string;
  tool_parameters: Record<string, unknown>;
  success: boolean;
  error_message?: string;
  mcp_client_user_agent?: string;
  mcp_server_version?: string;
  transport: string;
  duration_ms?: number;
  team_id?: number;
  user_id?: number;
  styleguide_id?: number;
  mcp_auth_id?: number;
  viewer_id?: string;
  oauth_client_id?: string;
  client_id?: string;
  mcp_consumer_id?: string;
}

export function getAmplitudeUserId(
  credentials: Credentials,
): string | undefined {
  if (credentials.type === "client_token") return undefined;
  if (credentials.userId) return `${credentials.userId}`;
  return undefined;
}

export function getAmplitudeDeviceId(
  credentials: Credentials,
): string | undefined {
  if (credentials.type === "client_token") return undefined;
  if (credentials.type === "jwt" && credentials.viewerId)
    return `mcp-url-${credentials.viewerId}`;
  if (credentials.type === "oauth_jwt" && credentials.viewerId)
    return `mcp-oauth-${credentials.viewerId}`;
  return undefined;
}

function sendMcpLog(payload: McpLogPayload, credentials: Credentials): void {
  const promise = isLocalMCP(credentials)
    ? request(
        "/mcp/log",
        credentials,
        { method: "POST", body: JSON.stringify(payload) },
        undefined,
        ApiType.Internal,
      )
    : internalRequest("/mcp/log", "POST", payload);

  promise.catch((err) => log.error("Failed to send MCP log:", err));
}

function buildLogPayload(
  toolName: string,
  toolParameters: Record<string, unknown>,
  credentials: Credentials,
  clientUserAgent: string | undefined,
  serverVersion: string,
  durationMs: number,
  success: boolean,
  errorMessage?: string,
): McpLogPayload {
  const loggedToolParameters = { ...toolParameters };
  if (credentials.type === "oauth_jwt" && credentials.accessRestrictedReason) {
    loggedToolParameters.access_restricted_reason =
      credentials.accessRestrictedReason;
  }

  const base: McpLogPayload = {
    tool_name: toolName,
    tool_parameters: loggedToolParameters,
    success,
    transport: isLocalMCP(credentials) ? "local" : "remote",
    mcp_client_user_agent: clientUserAgent,
    mcp_server_version: serverVersion,
    duration_ms: durationMs,
  };

  if (errorMessage !== undefined) {
    base.error_message = errorMessage;
  }

  if (credentials.type === "jwt") {
    base.team_id = credentials.teamId;
    base.user_id = credentials.userId;
    base.styleguide_id = credentials.styleguideId;
    base.mcp_auth_id = credentials.mcpAuthId;
    base.viewer_id = credentials.viewerId;
  } else if (credentials.type === "oauth_jwt") {
    base.team_id = credentials.teamId;
    base.user_id = credentials.userId;
    base.oauth_client_id = credentials.clientId;
    base.mcp_consumer_id = credentials.viewerId;
  } else if (credentials.type === "client_token") {
    base.client_id = credentials.client;
  }

  return base;
}

function sanitizeToolParams(
  toolName: string,
  params: Record<string, unknown>,
): Record<string, unknown> {
  const sanitized = { ...params };
  if (toolName === "lint-code") {
    delete sanitized.code;
  }
  return sanitized;
}

// Lambda timeout is 30s (defined in app-stack.ts)
// when we approach this, log the tool call so we can investigate why
const TOOL_TIMEOUT_WARNING_MS = 28_000;
async function runWithTimeoutWarning<T>(
  toolName: string,
  toolArgs: Record<string, unknown>,
  fn: () => T | Promise<T>,
): Promise<T> {
  if (!process.env["SENTRY_DSN"]) return fn();

  const startTime = Date.now();
  const timer = setTimeout(() => {
    captureMessage("MCP call approaching timeout", {
      level: "warning",
      tags: {
        tool_name: toolName,
        tool_args: JSON.stringify(toolArgs),
        elapsed_seconds: (Date.now() - startTime) / 1000,
      },
    });
    Sentry.flush(1500).catch(() => {});
  }, TOOL_TIMEOUT_WARNING_MS);

  try {
    return await fn();
  } finally {
    clearTimeout(timer);
  }
}

type Map<T> = T extends ZodRawShape ? { [K in keyof T]: output<T[K]> } : never;

type inferOutput<T> = T extends ZodRawShape ? Map<T> : output<T>;

export type TrackedToolCallback<
  Args extends ZodRawShape | ZodType | undefined,
> = (
  args: inferOutput<Args>,
  context: RequestHandlerExtra<ServerRequest, ServerNotification>,
  clientUserAgent: string | undefined,
) => CallToolResult | Promise<CallToolResult>;

/**
 * Wraps a tool handler to track tool calls via Amplitude and DB logging.
 * Resolves the client user agent centrally and passes it to the handler.
 */
export function registerTrackedToolCall<
  OutputArgs extends ZodRawShape | ZodType,
  InputArgs extends undefined | ZodRawShape | ZodType = undefined,
>(
  server: McpServer,
  name: string,
  config: {
    title?: string;
    description?: string;
    inputSchema?: InputArgs;
    outputSchema?: OutputArgs;
    annotations?: ToolAnnotations;
    _meta?: Record<string, unknown>;
  },
  handler: TrackedToolCallback<InputArgs>,
) {
  server.registerTool<OutputArgs, InputArgs>(name, config, (async (
    // @ts-expect-error - implicit any from generic type
    args,
    // @ts-expect-error - implicit any from generic type
    context,
  ) => {
    const clientVersion = server.server.getClientVersion();
    const fallbackUserAgent = clientVersion
      ? `${clientVersion.name}/${clientVersion.version}`
      : undefined;
    const userAgent = getUserAgent(
      context.requestInfo?.headers,
      fallbackUserAgent,
    );

    const credentials = await getCredentials(context.authInfo);
    setRequestUser(credentials);

    const sanitizedArgs = sanitizeToolParams(name, args);
    setActiveTool(name, sanitizedArgs);

    const startTime = Date.now();
    // When Rails restricted access, surface the reason instead of calling the
    // API. Rails enforces it regardless; this is purely UX.
    const restrictedMessage = accessRestrictedMessage(credentials);
    const result: CallToolResult = restrictedMessage
      ? {
          isError: true,
          content: [{ type: "text", text: restrictedMessage }],
        }
      : await runWithTimeoutWarning(name, sanitizedArgs, () =>
          handler(args as any, context, userAgent),
        );
    const durationMs = Date.now() - startTime;

    const isError = result.isError ?? false;
    const errorMessage = isError
      ? result.content
          .filter((item) => item.type === "text")
          .map((item) => item.text)
          .join("\n")
      : undefined;

    if (enabled) {
      const eventProperties: EventProperties = {
        tool_name: name,
        tool_parameters: sanitizedArgs,
        success: !isError,
        progress_token: context._meta?.progressToken,
        mcp_client_user_agent: userAgent,
        duration_ms: durationMs,
      };

      // @todo: Enable tracking for `oauth_jwt` once we have unique ID to use for McpConsumers
      // Currently McpConsumers have an int id which will clash with normal Users in Amplitude
      if (credentials.type !== "client_token") {
        const userId = getAmplitudeUserId(credentials);
        const deviceId = getAmplitudeDeviceId(credentials);
        if (userId || deviceId) {
          track({
            event_type: "mcp:tool_call",
            event_properties: eventProperties,
            user_properties: {
              team_id: credentials.teamId,
              viewer_id: credentials.viewerId,
              styleguide_id: credentials.styleguideId,
            } satisfies UserProperties,
            user_agent: userAgent,
            user_id: userId,
            device_id: deviceId,
          });
        } else {
          captureMessage("Amplitude event skipped: missing user identifier", {
            level: "warning",
            extra: {
              tool_name: name,
              credentials_type: credentials.type,
              auth_id: credentials.mcpAuthId,
              client_id: credentials.clientId,
            },
          });
        }
      }
    }

    sendMcpLog(
      buildLogPayload(
        name,
        sanitizedArgs,
        credentials,
        userAgent,
        packageJson.version,
        durationMs,
        !isError,
        errorMessage,
      ),
      credentials,
    );

    return result;
  }) as ToolCallback<InputArgs>);
}

export function registerTrackedResource(
  server: McpServer,
  name: string,
  template: ResourceTemplate,
  metadata: ResourceMetadata,
  handler: ReadResourceTemplateCallback,
) {
  server.registerResource(
    name,
    template,
    metadata,
    async (uri, variables, extra) => {
      const clientVersion = server.server.getClientVersion();
      const fallbackUserAgent = clientVersion
        ? `${clientVersion.name}/${clientVersion.version}`
        : undefined;
      const userAgent = getUserAgent(
        extra.requestInfo?.headers,
        fallbackUserAgent,
      );

      const credentials = await getCredentials(extra.authInfo);
      setRequestUser(credentials);

      const startTime = Date.now();
      let result: ReadResourceResult;
      let errorMessage: string | undefined;
      let success = true;

      try {
        result = await handler(uri, variables, extra);
      } catch (err) {
        success = false;
        errorMessage = err instanceof Error ? err.message : String(err);
        throw err;
      } finally {
        const durationMs = Date.now() - startTime;
        const resourceParams: Record<string, unknown> = { ...variables };

        if (enabled) {
          const eventProperties: EventProperties = {
            tool_name: `resource:${name}`,
            tool_parameters: resourceParams,
            success,
            progress_token: extra._meta?.progressToken,
            mcp_client_user_agent: userAgent,
            duration_ms: durationMs,
          };

          // @todo: Enable tracking for `oauth_jwt` once we have unique ID to use for McpConsumers
          // Currently McpConsumers have an int id which will clash with normal Users in Amplitude
          if (credentials.type != "client_token") {
            const userId = getAmplitudeUserId(credentials);
            const deviceId = getAmplitudeDeviceId(credentials);
            if (userId || deviceId) {
              track({
                event_type: "mcp:resource_read",
                event_properties: eventProperties,
                user_properties: {
                  team_id: credentials.teamId,
                  viewer_id: credentials.viewerId,
                  styleguide_id: credentials.styleguideId,
                } satisfies UserProperties,
                user_agent: userAgent,
                user_id: userId,
                device_id: deviceId,
              });
            } else {
              captureMessage(
                "Amplitude event skipped: missing user identifier",
                {
                  level: "warning",
                  extra: {
                    resource_name: name,
                    credentials_type: credentials.type,
                    auth_id: credentials.mcpAuthId,
                  },
                },
              );
            }
          }

          sendMcpLog(
            buildLogPayload(
              `resource:${name}`,
              resourceParams,
              credentials,
              userAgent,
              packageJson.version,
              durationMs,
              success,
              errorMessage,
            ),
            credentials,
          );
        }

        return result!;
      }
    },
  );
}

const getUserAgent = (
  headers: Record<string, string | string[] | undefined> | undefined,
  fallback?: string,
) => {
  if (headers) {
    if (Array.isArray(headers["user-agent"])) {
      return headers["user-agent"].join(" ");
    }
    if (typeof headers["user-agent"] === "string") {
      return headers["user-agent"];
    }
  }
  return fallback;
};
